// src/api/v3/payments-api-v3.js
const API_BASE = process.env.REACT_APP_API_BASE ;

// --- Helper to parse responses safely ---
async function handleRes(res) {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = await res.json();
      msg = json?.errors?.join(', ') || json?.error || msg;
    } catch {
      // not JSON (HTML error pages)
    }
    throw new Error(msg);
  }

  // Try parsing as JSON
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function jsonReq(url, opts = {}) {
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...opts,
    });
    if (!res.ok) throw new Error((await res.text()) || 'Request failed');
    return res.json();
  }

const PaymentsAPI = {
  /**
   * === GET /api/v3/payments
   * Optional params: { status, order_id, customer_id }
   */
  async list(params = {}) {
    const url = new URL(`${API_BASE}/api/v3/payments`);
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.set(k, v);
    });

    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    return handleRes(res);
  },

  /**
   * === GET /api/v3/payments/proofs
   * Used by finance to review pending proofs
   */
  async proofs() {
    const res = await fetch(`${API_BASE}/api/v3/payments/proofs`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    return handleRes(res);
  },

  /**
   * === GET /api/v3/payments/:id
   * Fetch single payment detail
   */
  async show(id) {
    const res = await fetch(`${API_BASE}/api/v3/payments/${id}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    return handleRes(res);
  },

  /**
   * === PUT /api/v3/payments/:id
   * Update payment status or fields
   */
  async update(id, data) {
    const res = await fetch(`${API_BASE}/api/v3/payments/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleRes(res);
  },

  /**
   * === POST /api/v3/payments/:id/upload_receipt
   * Upload proof image directly (multipart)
   * Automatically sets status to :awaiting_review on backend
   */
  async uploadReceipt(id, file) {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_BASE}/api/v3/payments/${id}/upload_receipt`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    return handleRes(res);
  },
   addReceipt(paymentId, { url, note, make_primary = true }) {
    return jsonReq(`${API_BASE}/api/v3/payments/${paymentId}/receipts`, {
      method: 'POST',
      body: JSON.stringify({ url, note, make_primary }),
    });
  },
  setPrimaryReceipt(paymentId, receiptId) {
    return jsonReq(`${API_BASE}/api/v3/payments/${paymentId}/receipts/${receiptId}/primary`, {
      method: 'PATCH',
    });
  },
  deleteReceipt(paymentId, receiptId) {
    return jsonReq(`${API_BASE}/api/v3/payments/${paymentId}/receipts/${receiptId}`, {
      method: 'DELETE',
    });
  },
  // === GET /api/v3/payments/export
  // params: from, to, status, customer_id
  exportCSV(params = {}) {
    const url = new URL(`${API_BASE}/api/v3/payments/export`);
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.set(k, v);
    });
    // open in a new tab to trigger download
    window.open(url.toString(), '_blank');
  },
   uploadReceipts(paymentId, files, note = '') {
    const fd = new FormData();
    // allow both FileList and Array<File>
    Array.from(files || []).forEach(f => fd.append('files[]', f));
    if (note) fd.append('note', note);
    return fetch(`${API_BASE}/api/v3/payments/${paymentId}/upload_receipts`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(handleRes);
  },
};

export default PaymentsAPI;
